/**
 * @fileoverview AR-924/AR-932 — One row of the step-2 table: thumbnail, name + meta, students select and
 * match badge. The select takes several students (group work: one file sent to each member) and offers
 * "All students" as its first option for the whole class.
 */

import { Box, Group, MultiSelect, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { formatFileSize } from '../teacherUpload'
import { resolveStudentSelection, selectValueOf, withAllStudentsOption } from './allStudentsOption'
import { MatchStatusCell } from './MatchStatusCell'
import { StagedThumb } from './StagedThumb'
import { coversEveryone, type StagedItem } from './stagedItems'

/** Wider than the Figma 180px so several full names fit without truncation. */
const SELECT_WIDTH = 320
const NEEDS_STUDENT_ROW_BACKGROUND = 'var(--mantine-color-yellow-0, #fff9db)'
const NEEDS_STUDENT_INPUT = { input: { borderColor: 'var(--mantine-color-orange-5, #fd7e14)' } }

interface MatchRowProps {
  readonly item: StagedItem
  readonly groupedWithName: string | null
  readonly studentOptions: { value: string; label: string }[]
  readonly disabled: boolean
  readonly onAssign: (id: string, studentIds: readonly string[]) => void
}

export function MatchRow({ item, groupedWithName, studentOptions, disabled, onAssign }: MatchRowProps) {
  const { t } = useTranslation()
  const tr = (key: string, options?: object) => t(`task_tracker.upload_for_class.${key}`, options)
  const everyone = studentOptions.map(option => option.value)
  const wholeClass = coversEveryone(item.studentIds, everyone)
  const needsStudent = item.studentIds.length === 0
  const metaParts = item.kind === 'link'
    ? [tr('link_added_in_step1')]
    : [t(`task_tracker.teacher_upload.file_kind_${item.fileKind}`), formatFileSize(item.sizeInBytes)]
  if (groupedWithName) metaParts.push(tr('grouped_with', { name: groupedWithName }))

  return (
    <tr style={needsStudent ? { background: NEEDS_STUDENT_ROW_BACKGROUND } : undefined}>
      <td>
        <Group spacing="sm" noWrap>
          <StagedThumb item={item} />
          <Box sx={{ minWidth: 0 }}>
            <Text size="sm" weight={600} truncate>{item.name}</Text>
            <Text size="xs" color="dimmed">{metaParts.join(' · ')}</Text>
          </Box>
        </Group>
      </td>
      <td>
        <MultiSelect
          data={withAllStudentsOption(studentOptions, tr('all_students'))}
          value={selectValueOf(item.studentIds, everyone)}
          onChange={value => onAssign(item.id, resolveStudentSelection(item.studentIds, value, everyone))}
          placeholder={needsStudent ? tr('select_student') : undefined}
          searchable
          clearable
          withinPortal
          disabled={disabled}
          size="sm"
          styles={needsStudent ? NEEDS_STUDENT_INPUT : undefined}
          aria-label={tr('col_student')}
          sx={{ width: SELECT_WIDTH }}
        />
      </td>
      <td>
        <MatchStatusCell item={item} wholeClass={wholeClass} />
      </td>
    </tr>
  )
}
